import { forwardRef, useRef, useState } from "react";
import { Popover, Whisper } from "rsuite";
import { OverlayTriggerHandle } from "rsuite/esm/internals/Overlay/OverlayTrigger";
import AddFolderModal from "./AddFolderModal";
import AddExternalLinkModal from "./AddExternalLinkModal";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import DropdownMenuDivider from "../../Menus/DropdownMenuDivider";
import { useAtom, useAtomValue } from "jotai";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { sidebarOpenAtom } from "../../../atoms/SidebarAtoms";
import AddPageDrawer from "../../Drawers/AddPageDrawer";
import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import CloudAppDrawer from "../../Drawers/CloudAppDrawer";
import { activeLinkAtom } from "../../../atoms/SidebarAtoms";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import CloudAppUtils from "../../../utils/CloudAppUtils";
import useWorkspace from "../../../api/api-hooks/workspaces/useWorkspace";
import { IsMobileAtom } from "../../../App";
import { useLocation, useNavigate } from "react-router-dom";

interface AddOptionsProps {
  handleOpenModal: (type: string | null) => void;
  handleAddApp: () => void;
  disabled: boolean;
}

interface AddButtonProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  open?: boolean;
}

const AddOptions = ({ handleOpenModal, disabled }: AddOptionsProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canCreate } = CloudAppUtils.getCloudAppsPermissions();

  return (
    <PopoverDropdownMenu>
      <DropdownOptionWithIcon
        name="Add Page"
        onClick={() => handleOpenModal("AddPage")}
        icon="mdi mdi-file-table-box-outline"
        disabled={disabled}
      />
      <DropdownOptionWithIcon
        name="Add Folder"
        onClick={() => handleOpenModal("AddFolder")}
        icon="mdi mdi-folder-outline"
        disabled={disabled}
      />
      <DropdownOptionWithIcon
        name="Add External Link"
        onClick={() => handleOpenModal("AddExternalLink")}
        icon="mdi mdi-open-in-new"
        disabled={disabled}
      />
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="GrowthCloud Store"
        onClick={() => {
          handleOpenModal(null);
          navigate(`${pathname}?store=true&page=installed-apps`);
        }}
        icon="mdi mdi-shopping"
        disabled={!canCreate}
      />
    </PopoverDropdownMenu>
  );
};

const SidebarAdd: React.FC = () => {
  const iconLinkRef = useRef<OverlayTriggerHandle>(null);
  const navigate = useNavigate();
  const [tenantID] = useAtom(tenantIDAtom);
  const [open] = useAtom(sidebarOpenAtom);
  const [activeLink, setActiveLink] = useAtom(activeLinkAtom);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const { wPermissions } = useWorkspacePermissions();
  const { cPermissions } = useCloudPermissions();
  const smallScreen = useAtomValue(IsMobileAtom);
  const { workspace } = useWorkspace();
  const disabled = !workspace;

  const closeModal = () => setOpenModal(null);
  const closePopover = () => iconLinkRef.current?.close();

  const handleOpenModal = (modalName: string | null) => {
    setOpenModal(modalName);
    closePopover();
  };

  const handleAddApp = () => {
    navigate(`/clouds/${tenantID}/settings/cloudapps/marketplace`);
    closePopover();
  };

  const baseSidebarLinkCss =
    "tw:text-[13px] tw:transition tw:delay-50 tw:duration-300 tw:ease-in-out tw:hover:bg-sidebar-link tw:font-medium tw:text-dark tw:cursor-pointer tw:rounded-md tw:block tw:px-3 tw:py-2 tw:no-underline";

  const IconWrapperProps = open
    ? {
        className: `tw:px-4 tw:my-2 tw:uppercase tw:font-semibold tw:text-sm tw:text-light tw:flex tw:items-center tw:justify-between tw:border-transparent ${
          !open && "tw:hidden"
        }`,
      }
    : {
        className: `${baseSidebarLinkCss} tw:flex tw:justify-center tw:items-center tw:h-[34px] tw:mx-3 tw:border-1 tw:border-lighter`,
      };

  return (
    <>
      {open && <hr className="tw:mt-1 tw:mb-2" />}
      <div {...IconWrapperProps}>
        {open && <>Apps &amp; Pages</>}
        <Whisper
          preventOverflow
          placement={smallScreen ? undefined : "autoHorizontalStart"}
          controlId="add-options"
          trigger={disabled ? undefined : "click"}
          ref={iconLinkRef}
          onClose={() => setActiveLink(null)}
          speaker={
            <Popover className="sidebar-popover">
              <AddOptions
                handleOpenModal={handleOpenModal}
                handleAddApp={handleAddApp}
                disabled={
                  !wPermissions.workspace_create_page &&
                  !cPermissions.cloud_create_page
                }
              />
            </Popover>
          }
        >
          <AddButton
            active={activeLink === "Add"}
            disabled={disabled}
            onClick={() => setActiveLink("Add")}
            open={open}
          />
        </Whisper>
      </div>
      {!open && <hr className="tw:mt-3 tw:mb-2" />}
      <AddPageDrawer open={openModal === "AddPage"} handleClose={closeModal} />
      <AddFolderModal
        open={openModal === "AddFolder"}
        handleClose={closeModal}
      />
      <AddExternalLinkModal
        open={openModal === "AddExternalLink"}
        handleClose={closeModal}
      />
      <CloudAppDrawer
        open={openModal === "CloudAppDrawer"}
        onClose={closeModal}
        type="Create"
      />
    </>
  );
};

export const AddButton = forwardRef<HTMLSpanElement, AddButtonProps>(
  function AddButton({ active, disabled, onClick, open }, ref) {
    return (
      <i
        ref={ref}
        className={`mdi mdi-plus tw:text-dark
        ${
          open &&
          "tw:w-[27px] tw:h-[27px] tw:border tw:transition tw:delay-50 tw:duration-300 tw:ease-in-out tw:border-lighter tw:hover:bg-sidebar-link tw:rounded-sm tw:cursor-pointer"
        }
        ${active && open ? "tw:border-lighter tw:bg-sidebar-link" : ""}`}
        style={{ fontSize: 22, lineHeight: 1, padding: 1 }}
        onClick={() => {
          if (disabled) return;
          onClick();
        }}
      />
    );
  }
);

export default SidebarAdd;
